import { State } from "..";

import { AbstractStore } from ".";

/**
 * Possible noise suppresion states. Browser is browser noise suppresion and enhanced is machine learning suppression via RNNoise.
 */
export type NoiseSuppresionState = "disabled" | "browser" | "enhanced";

const NoiseSuppresionStates: NoiseSuppresionState[] = [
  "disabled",
  "browser",
  "enhanced",
];

/**
 * Possible screen share qualities. Low is 720p@30fps, low60 is 720p@60fps,
 * high is 1080p@30fps, and high60 is 1080p@60fps.
 *
 * The upstream "text" mode (source resolution at 5 fps) is removed: it only
 * appeared on instances whose video_resolution limit allows 1080p, and it is
 * far too easy to select by accident and then assume the client is broken.
 *
 * 60fps presets used to exist at both 720p and 1080p, then were removed
 * entirely: Chromium's desktop-capture governor caps a 1440p-class source
 * around 28 fps regardless of the target framerate requested, so neither
 * preset could actually deliver 60fps through that path.
 *
 * `high60` came back because a native GPU capture path (Windows, window
 * shares only, shipped from `for-desktop`) does not go through that governor
 * and can genuinely reach 60fps there. The option is offered on every
 * platform regardless, deliberately, not gated on that capability -- on any
 * other path it just raises the bitrate ceiling (see `screenShareEncoding` in
 * `rtc/state.tsx`) with no extra frames to show for it, which is what the
 * weak-link warning there exists to catch.
 *
 * `low60` exists for the same reason and carries the same caveat, just at
 * 720p: it is real 60fps only on that native Windows capture path, and
 * everywhere else it is a bitrate-ceiling bump with nothing extra to show for
 * it. Unlike `high`/`high60`, it is never gated on `limits().video_resolution`
 * -- a 720p limit already permits it, since that limit gates by resolution,
 * not framerate (see `getEnabledScreenShareQualities` in `rtc/state.tsx`).
 */
export type ScreenShareQualityName = "low" | "low60" | "high" | "high60";

/**
 * Array of available screen share quality names, ordered low to high --
 * `#screenShareQuality()` in `rtc/state.tsx` walks backward through this
 * order to find the closest enabled quality to a saved one that is no
 * longer offered.
 */
export const ScreenShareQualityNames: ScreenShareQualityName[] = [
  "low",
  "low60",
  "high",
  "high60",
];

export interface TypeVoice {
  preferredAudioInputDevice?: string;
  preferredAudioOutputDevice?: string;
  preferredVideoDevice?: string;

  echoCancellation: boolean;
  noiseSupression: NoiseSuppresionState;
  autoGainControl: boolean;

  screenShareQuality: ScreenShareQualityName;
  screenShareQualityAsk: boolean;
  screenShareAudio: boolean;

  inputVolume: number;
  outputVolume: number;
  deafen: boolean;
  micOn: boolean;

  userVolumes: Record<string, number>;
  userMutes: Record<string, boolean>;

  screenShareVolumes: Record<string, number>;
  screenShareMutes: Record<string, boolean>;

  /** Screen shares the user has explicitly chosen to watch, keyed by user id */
  screenShareWatching: Record<string, boolean>;
  /** Show the playback statistics overlay on screen shares */
  screenShareStats: boolean;

  /**
   * Show participants that have no camera/screen share in the call grid.
   * Off = only tiles that carry live video (Discord's "Show non-video
   * participants" toggle).
   */
  showNonVideoParticipants: boolean;
  /**
   * Hide the text chat while in a call so the call card fills the channel
   * area (an expanded view that is not fullscreen).
   */
  hideChatInCall: boolean;

  /** Volume applied to every soundboard sound (yours included) */
  soundboardVolume: number;
  /** Mute all soundboard sounds */
  soundboardMuted: boolean;
  /** Users whose soundboard sounds are muted, keyed by user id */
  soundboardUserMutes: Record<string, boolean>;
}

/**
 * Handles enabling and disabling client experiments.
 */
export class Voice extends AbstractStore<"voice", TypeVoice> {
  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "voice");
  }

  /**
   * Hydrate external context
   */
  hydrate(): void {
    /** nothing needs to be done */
  }

  /**
   * Generate default values
   */
  default(): TypeVoice {
    return {
      echoCancellation: true,
      noiseSupression: "enhanced",
      autoGainControl: true,
      screenShareQuality: "low",
      screenShareQualityAsk: true,
      screenShareAudio: true,
      inputVolume: 1.0,
      outputVolume: 1.0,
      deafen: false,
      micOn: true,
      userVolumes: {},
      userMutes: {},
      screenShareVolumes: {},
      screenShareMutes: {},
      screenShareWatching: {},
      screenShareStats: false,
      showNonVideoParticipants: true,
      hideChatInCall: false,
      soundboardVolume: 1.0,
      soundboardMuted: false,
      soundboardUserMutes: {},
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeVoice>): TypeVoice {
    const data = this.default();

    if (typeof input.preferredAudioInputDevice === "string") {
      data.preferredAudioInputDevice = input.preferredAudioInputDevice;
    }

    if (typeof input.preferredAudioOutputDevice === "string") {
      data.preferredAudioOutputDevice = input.preferredAudioOutputDevice;
    }

    if (typeof input.preferredVideoDevice === "string") {
      data.preferredVideoDevice = input.preferredVideoDevice;
    }

    if (typeof input.echoCancellation === "boolean") {
      data.echoCancellation = input.echoCancellation;
    }

    // migrate legacy noise suppression to new suppression state
    if ((input.noiseSupression as unknown) === "true") {
      data.noiseSupression = "enhanced";
    } else if ((input.noiseSupression as unknown) === "false") {
      data.noiseSupression = "disabled";
    } else if (
      input.noiseSupression &&
      NoiseSuppresionStates.includes(input.noiseSupression)
    ) {
      data.noiseSupression = input.noiseSupression;
    }

    if (typeof input.autoGainControl === "boolean") {
      data.autoGainControl = input.autoGainControl;
    }

    // "low60" and "high60" are both real, currently-enabled quality names,
    // so they fall through to the ScreenShareQualityNames.includes branch
    // below like any other valid value -- no remapping needed.
    if (
      input.screenShareQuality &&
      ScreenShareQualityNames.includes(input.screenShareQuality)
    ) {
      data.screenShareQuality = input.screenShareQuality;
    }

    if (typeof input.screenShareQualityAsk === "boolean") {
      data.screenShareQualityAsk = input.screenShareQualityAsk;
    }

    if (typeof input.screenShareAudio === "boolean") {
      data.screenShareAudio = input.screenShareAudio;
    }

    if (typeof input.inputVolume === "number") {
      data.inputVolume = input.inputVolume;
    }

    if (typeof input.outputVolume === "number") {
      data.outputVolume = input.outputVolume;
    }

    if (typeof input.deafen === "boolean") {
      data.deafen = input.deafen;
    }

    if (typeof input.micOn === "boolean") {
      data.micOn = input.micOn;
    }

    if (typeof input.userVolumes === "object") {
      Object.entries(input.userVolumes)
        .filter(
          ([userId, volume]) =>
            typeof userId === "string" && typeof volume === "number",
        )
        .forEach(([k, v]) => (data.userVolumes[k] = v));
    }

    if (typeof input.screenShareWatching === "object") {
      Object.entries(input.screenShareWatching)
        .filter(
          ([userId, watching]) =>
            typeof userId === "string" && watching === true,
        )
        .forEach(([k, v]) => (data.screenShareWatching[k] = v));
    }

    if (typeof input.screenShareStats === "boolean") {
      data.screenShareStats = input.screenShareStats;
    }

    if (typeof input.showNonVideoParticipants === "boolean") {
      data.showNonVideoParticipants = input.showNonVideoParticipants;
    }

    if (typeof input.hideChatInCall === "boolean") {
      data.hideChatInCall = input.hideChatInCall;
    }

    if (typeof input.soundboardVolume === "number") {
      data.soundboardVolume = input.soundboardVolume;
    }

    if (typeof input.soundboardMuted === "boolean") {
      data.soundboardMuted = input.soundboardMuted;
    }

    if (typeof input.soundboardUserMutes === "object") {
      Object.entries(input.soundboardUserMutes)
        .filter(
          ([userId, muted]) => typeof userId === "string" && muted === true,
        )
        .forEach(([k, v]) => (data.soundboardUserMutes[k] = v));
    }

    if (typeof input.userMutes === "object") {
      Object.entries(input.userMutes)
        .filter(
          ([userId, muted]) => typeof userId === "string" && muted === true,
        )
        .forEach(([k, v]) => (data.userMutes[k] = v));
    }

    if (typeof input.screenShareVolumes === "object") {
      Object.entries(input.screenShareVolumes)
        .filter(
          ([userId, volume]) =>
            typeof userId === "string" && typeof volume === "number",
        )
        .forEach(([k, v]) => (data.screenShareVolumes[k] = v));
    }

    if (typeof input.screenShareMutes === "object") {
      Object.entries(input.screenShareMutes)
        .filter(
          ([userId, muted]) => typeof userId === "string" && muted === true,
        )
        .forEach(([k, v]) => (data.screenShareMutes[k] = v));
    }

    return data;
  }

  /**
   * Set a user's volume
   * @param userId User ID
   * @param volume Volume
   */
  setUserVolume(userId: string, volume: number) {
    this.set("userVolumes", userId, volume);
  }

  /**
   * Get a user's volume
   * @param userId User ID
   * @returns Volume or default
   */
  getUserVolume(userId: string): number {
    // ?? rather than ||: 0 is a legitimate setting, and `||` turned a slider
    // dragged to zero back into full volume.
    return this.get().userVolumes[userId] ?? 1.0;
  }

  /**
   * Set whether a user is muted
   * @param userId User ID
   * @param muted Whether they should be muted
   */
  setUserMuted(userId: string, muted: boolean) {
    this.set("userMutes", userId, muted);
  }

  /**
   * Get whether a user is muted
   * @param userId User ID
   * @returns Whether muted
   */
  getUserMuted(userId: string): boolean {
    return this.get().userMutes[userId] || false;
  }

  /**
   * Set a user's screen share volume
   * @param userId User ID
   * @param volume Volume
   */
  setScreenShareVolume(userId: string, volume: number) {
    this.set("screenShareVolumes", userId, volume);
  }

  /**
   * Get a user's screen share volume
   * @param userId User ID
   * @returns Volume or default
   */
  getScreenShareVolume(userId: string): number {
    // ?? rather than ||, see getUserVolume
    return this.get().screenShareVolumes[userId] ?? 1.0;
  }

  /**
   * Set whether a user's screen share is muted
   * @param userId User ID
   * @param muted Whether they should be muted
   */
  setScreenShareMuted(userId: string, muted: boolean) {
    this.set("screenShareMutes", userId, muted);
  }

  /**
   * Get whether a user's screen share is muted.
   *
   * Defaults to false: audio only reaches you for shares you chose to watch
   * (see RoomAudioManager), so it does not also need to start muted -- that
   * only led to people watching a stream in silence and assuming it was broken.
   * @param userId User ID
   * @returns Whether muted
   */
  getScreenShareMuted(userId: string): boolean {
    return this.get().screenShareMutes[userId] ?? false;
  }

  /**
   * Set whether the user is watching a given screen share
   * @param userId User ID
   * @param watching Whether to subscribe to their screen share
   */
  setScreenShareWatching(userId: string, watching: boolean) {
    this.set("screenShareWatching", userId, watching);
  }

  /**
   * Get whether the user is watching a given screen share.
   *
   * Defaults to false: screen shares are opt-in, so joining a busy call does
   * not immediately pull several video streams you did not ask for.
   * @param userId User ID
   * @returns Whether watching
   */
  getScreenShareWatching(userId: string): boolean {
    return this.get().screenShareWatching[userId] ?? false;
  }

  /**
   * Whether the playback statistics overlay is enabled
   */
  get screenShareStats(): boolean {
    return this.get().screenShareStats;
  }

  /**
   * Toggle the playback statistics overlay
   */
  set screenShareStats(value: boolean) {
    this.set("screenShareStats", value);
  }

  /**
   * Whether participants without video are shown in the call grid
   */
  get showNonVideoParticipants(): boolean {
    return this.get().showNonVideoParticipants ?? true;
  }

  /**
   * Show or hide participants without video in the call grid
   */
  set showNonVideoParticipants(value: boolean) {
    this.set("showNonVideoParticipants", value);
  }

  /**
   * Whether the text chat is hidden while in a call
   */
  get hideChatInCall(): boolean {
    return this.get().hideChatInCall ?? false;
  }

  /**
   * Hide or show the text chat while in a call
   */
  set hideChatInCall(value: boolean) {
    this.set("hideChatInCall", value);
  }

  /**
   * Volume applied to every soundboard sound
   */
  get soundboardVolume(): number {
    return this.get().soundboardVolume ?? 1.0;
  }

  /**
   * Set the soundboard volume
   */
  set soundboardVolume(value: number) {
    this.set("soundboardVolume", value);
  }

  /**
   * Whether every soundboard sound is muted
   */
  get soundboardMuted(): boolean {
    return this.get().soundboardMuted ?? false;
  }

  /**
   * Mute or unmute every soundboard sound
   */
  set soundboardMuted(value: boolean) {
    this.set("soundboardMuted", value);
  }

  /**
   * Set whether a user's soundboard sounds are muted
   * @param userId User ID
   * @param muted Whether they should be muted
   */
  setSoundboardUserMuted(userId: string, muted: boolean) {
    this.set("soundboardUserMutes", userId, muted);
  }

  /**
   * Get whether a user's soundboard sounds are muted
   * @param userId User ID
   * @returns Whether muted
   */
  getSoundboardUserMuted(userId: string): boolean {
    return this.get().soundboardUserMutes?.[userId] || false;
  }

  /**
   * Set the preferred audio input device
   */
  set preferredAudioInputDevice(value: string | undefined) {
    this.set("preferredAudioInputDevice", value);
  }

  /**
   * Set the preferred audio output device
   */
  set preferredAudioOutputDevice(value: string | undefined) {
    this.set("preferredAudioOutputDevice", value);
  }

  /**
   * Set the preferred video input device
   */
  set preferredVideoDevice(value: string | undefined) {
    this.set("preferredVideoDevice", value);
  }

  /**
   * Set echo cancellation
   */
  set echoCancellation(value: boolean) {
    this.set("echoCancellation", value);
  }

  /**
   * Set noise cancellation
   */
  set noiseSupression(value: NoiseSuppresionState) {
    this.set("noiseSupression", value);
  }

  /**
   * Set auto gain control
   */
  set autoGainControl(value: boolean) {
    this.set("autoGainControl", value);
  }

  /**
   * Set screen share quality
   */
  set screenShareQuality(value: ScreenShareQualityName) {
    this.set("screenShareQuality", value);
  }

  /**
   * Set screen share quality always ask
   */
  set screenShareQualityAsk(value: boolean) {
    this.set("screenShareQualityAsk", value);
  }

  /**
   * Set screen share audio
   */
  set screenShareAudio(value: boolean) {
    this.set("screenShareAudio", value);
  }

  /**
   * Set input volume
   */
  set inputVolume(value: number) {
    this.set("inputVolume", value);
  }

  /**
   * Set output volume
   */
  set outputVolume(value: number) {
    this.set("outputVolume", value);
  }

  /**
   * Set mic status
   */
  set micOn(value: boolean) {
    this.set("micOn", value);
  }

  /**
   * Set deafen status
   */
  set deafen(value: boolean) {
    this.set("deafen", value);
  }

  /**
   * Get the preferred audio input device
   */
  get preferredAudioInputDevice(): string | undefined {
    return this.get().preferredAudioInputDevice;
  }

  /**
   * Get the preferred audio output device
   */
  get preferredAudioOutputDevice(): string | undefined {
    return this.get().preferredAudioOutputDevice;
  }

  /**
   * Get the preferred video input device
   */
  get preferredVideoDevice(): string | undefined {
    return this.get().preferredVideoDevice;
  }

  /**
   * Get echo cancellation
   */
  get echoCancellation(): boolean | undefined {
    return this.get().echoCancellation;
  }

  /**
   * Get noise supression
   */
  get noiseSupression(): NoiseSuppresionState | undefined {
    return this.get().noiseSupression;
  }

  /**
   * Get auto gain control
   */
  get autoGainControl(): boolean | undefined {
    return this.get().autoGainControl;
  }

  /**
   * Get screen share quality
   */
  get screenShareQuality(): ScreenShareQualityName | undefined {
    return this.get().screenShareQuality;
  }

  /**
   * Get screen share quality always ask
   */
  get screenShareQualityAsk(): boolean {
    return this.get().screenShareQualityAsk;
  }

  /**
   * Get screen share audio
   */
  get screenShareAudio(): boolean {
    return this.get().screenShareAudio;
  }

  /**
   * Get input volume
   */
  get inputVolume(): number {
    return this.get().inputVolume;
  }

  /**
   * Get output volume
   */
  get outputVolume(): number {
    return this.get().outputVolume;
  }

  /**
   * Get deafen status
   */
  get deafen(): boolean {
    return this.get().deafen;
  }

  /**
   * Get mic status
   */
  get micOn(): boolean {
    return this.get().micOn;
  }
}
